import { InjectionToken } from '@angular/core';
import { LotType } from '@core/api/angular';

export enum AddressSearchAction {
  Advanced = 'advanced',
  PFI = 'pfi',
}

export const addressSearchActionDescription = {
  [AddressSearchAction.Advanced]: 'Advanced',
  [AddressSearchAction.PFI]: 'PFI',
};

export enum AddressSearchType {
  LOCALITY = 'locality',
  ROAD = 'road',
  ADDRESS = 'address',
}

export const ADVANCED_SEARCH_OPTIONS = new InjectionToken<
  IAdvancedSearchOption[]
>('ADVANCED_SEARCH_OPTIONS', {
  factory: () => [
    {
      label: 'Address',
      href: 'address-search',
    },
    {
      label: 'Coordinates',
      href: 'coordinates-search',
    },
    {
      label: 'Crown Description',
      href: 'crown-description-search',
    },
    {
      label: 'Lot',
      href: 'lot',
      description: 'Plan & Street',
    },
    {
      label: 'Parcel',
      href: 'parcel',
      description: 'PFI, SPI & VIEW_PFI',
    },
    {
      label: 'Property',
      href: 'property',
      description: 'PFI, VIEW_PFI & Council No.',
    },
    {
      label: 'Survey Label',
      href: 'survey-label',
    },
    {
      label: 'Survey Mark',
      href: 'survey-mark',
    },
  ],
});

export interface IAdvancedSearchOption {
  label: string;
  href: string;
  description?: string;
}

/**
 * Advanced address search form
 */
export interface IAdvancedAddressSearchFormValues {
  unitNumber: string | null;
  number: string | null;
  streetName: string | null;
  streetType: string | null;
  streetSuffix: string | null;
  locality: string | null;
}

/**
 * query params for properties search
 */
export interface IAdvancedAddrSearchPropertyParams {
  roadName: string;
  roadType: string | null;
  roadSuffix: string | null;
  locality: string | null;
}

export interface IAdvancedAddressSearchRoadParamData {
  propertyParams: IAdvancedAddrSearchPropertyParams;
  roadInfo: IAdvancedAddressSearchRoadInfo;
}

export interface IAdvancedSearchAddressRoadOptions {
  roadName: string;
  roadType?: string;
  roadSuffix?: string;
  locality?: string;
}

export interface IAdvancedSearchAddressPropertyOptions {
  roadName: string;
  locality?: string;
  roadNumber?: string;
  unitNumber?: string;
  roadType?: string;
  roadSuffix?: string;
}

export interface IAdvancedAddressSearchRoadInfo {
  pfi: string;
  displayTitle: string;
}

export interface IAdvancedPropertySearchCouncilNoOptions {
  propertyNumber: string;
  lgaCode: string;
}

export interface IAdvancedLotSearchByPlanOptions {
  plan: string;
  lot?: string;
  type?: LotType;
}

export interface IAdvancedLotSearchByStreetOptions {
  lot: string;
  streetName: string;
  streetType?: string;
  streetSuffix?: string;
  locality?: string;
}

export interface IAdvancedSurveyLabelGeometrySearchOptions {
  pfiList: string[];
  surveyLabel: string;
}

export interface IAdvancedSurveyMarkSearchOptions {
  markName?: string;
  nineFigureNumber?: string;
  stampedId?: string;
}

export interface IAdvancedCrownDescriptionSimpleSearchOptions {
  parishOrTownshipCode: string;
  parishOrTownshipName: string;
}

export interface IAdvancedCrownDescriptionSearchOptions
  extends IAdvancedCrownDescriptionSimpleSearchOptions {
  allotment?: string;
  section?: string;
  subdivision?: string;
  portion?: string;
  block?: string;
}

export interface ICoordinatesRadius {
  label: string;
  value: number;
}

export interface IAdvancedCoordinatesSearchOptions {
  coordinateSearchType: CoordinatesSearchType;
  eastingOrLatitude: string;
  northingOrLongitude: string;
  mapRadius: number;
}

export const MAP_RADIUS_LIST: ICoordinatesRadius[] = [
  {
    label: '100m',
    value: 100,
  },
  {
    label: '200m',
    value: 200,
  },
  {
    label: '500m',
    value: 500,
  },
  {
    label: '1km',
    value: 1_000,
  },
  {
    label: '2km',
    value: 2_000,
  },
  {
    label: '5km',
    value: 5_000,
  },
  {
    label: '10km',
    value: 10_000,
  },
  {
    label: '20km',
    value: 20_000,
  },
  {
    label: '50km',
    value: 50_000,
  },
];

export enum CoordinatesSearchType {
  GDA2020_GEOGRAPHICALS = 'GDA2020 Geographicals',
  MGA2020_ZONE_54 = 'MGA2020 Zone 54',
  MGA2020_ZONE_55 = 'MGA2020 Zone 55',
}

export type CoordinatesType = CoordinatesSearchType | '54' | '55';
